import { CalendarIcon } from "@radix-ui/react-icons";
import dayjs from "dayjs";
import type { ControllerRenderProps } from "react-hook-form";
import { FormControl } from "@patient-watch/ui/form";
import { Button } from "@patient-watch/ui/button";
import { Calendar } from "@patient-watch/ui/calendar";
import { Input } from "@patient-watch/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@patient-watch/ui/popover";
import { cn } from "@patient-watch/ui";
import GroupedFields from "./GroupedFields";

export interface DateTimePickerProps {
    field?: ControllerRenderProps<any, any>;
    value?: Date | null;
    onChange?: (value: Date | null) => void;
    includeTime?: boolean;
    time?: string;
    onTimeChange?: (time: string) => void;
    disabled?: boolean;
    id?: string;
    className?: string;
    placeholder?: string;
}

function formatDisplayValue(value: Date | null | undefined, includeTime: boolean, time?: string) {
    if (!value) return null;
    if (includeTime && time) {
        return `${dayjs(value).format("DD/MM/YYYY")}, ${time}`;
    }
    return dayjs(value).format("D MMMM YYYY");
}

export default function DateTimePicker({
    field,
    value,
    onChange,
    includeTime = false,
    time,
    onTimeChange,
    disabled,
    id,
    className,
    placeholder = "Pick a date",
}: DateTimePickerProps) {
    const selected = field?.value ?? value ?? null;
    const handleDateChange = field?.onChange ?? onChange;
    const displayValue = formatDisplayValue(selected, includeTime, time);

    const datePicker = (
        <Popover>
            <PopoverTrigger asChild>
                {field ? (
                    <FormControl>
                        <Button
                            id={id}
                            variant="outline"
                            disabled={disabled}
                            className={cn(
                                "pl-3 w-full font-normal text-left",
                                !displayValue && "text-muted-foreground",
                                className
                            )}
                        >
                            {displayValue ?? <span>{placeholder}</span>}
                            <CalendarIcon className="opacity-50 ml-auto w-4 h-4" />
                        </Button>
                    </FormControl>
                ) : (
                    <Button
                        id={id}
                        variant="outline"
                        disabled={disabled}
                        className={cn(
                            "pl-3 w-full h-9 font-normal text-sm text-left",
                            !displayValue && "text-muted-foreground",
                            className
                        )}
                    >
                        {displayValue ?? <span>{placeholder}</span>}
                        <CalendarIcon className="opacity-50 ml-auto w-4 h-4" />
                    </Button>
                )}
            </PopoverTrigger>
            <PopoverContent className="p-0 w-auto" align="start">
                <Calendar
                    mode="single"
                    selected={selected ?? undefined}
                    onSelect={(date) => handleDateChange?.(date ?? null)}
                    disabled={(date) => date > new Date() || date < new Date("1900-01-01")}
                    initialFocus
                />
            </PopoverContent>
        </Popover>
    );

    if (!includeTime) {
        return datePicker;
    }

    return (
        <GroupedFields>
            {datePicker}
            <Input
                type="time"
                value={time ?? ""}
                onChange={(event) => onTimeChange?.(event.target.value)}
                disabled={disabled}
                className={cn("h-9 text-sm", className)}
            />
        </GroupedFields>
    );
}
